import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Typography, Grid, TextField, Button, CircularProgress, Box } from '@mui/material';
import { resetPassword } from '../../api/authApi';
import { requestPasswordReset } from '../../api/authApi';
import {
    CenteredGrid,
    StyledPaper,
    FormBox,
    SubmitButton,
    SuccessMessage,
    ErrorMessage,
} from './ResetPasswordPage.styles';

function ResetPassword() {
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get('code');

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    
    // Token validation states
    const [isValidatingToken, setIsValidatingToken] = useState(true);
    const [isTokenValid, setIsTokenValid] = useState(false);
    const [tokenError, setTokenError] = useState('');
    
    // Request new reset states
    const [isRequestingReset, setIsRequestingReset] = useState(false);
    const [resetRequestMessage, setResetRequestMessage] = useState('');

    // Validate token on component mount
    useEffect(() => {
        const validateToken = async () => {
            if (!token) {
                setTokenError('Invalid or missing reset token.');
                setIsValidatingToken(false);
                return;
            }

            try {
                // Validate token by making a simple API call
                const response = await fetch(`/api/auth/validate-reset-token?code=${token}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });
                
                if (!response.ok) {
                    if (response.status === 404) {
                        setTokenError('Password reset request expired or invalid. Please request a new password reset link.');
                    } else {
                        setTokenError('Invalid or missing reset token.');
                    }
                    setIsValidatingToken(false);
                    return;
                }
            } catch (err) {
                setTokenError('Invalid or missing reset token.');
                setIsValidatingToken(false);
                return;
            }
            
            // If we reach here, token is valid
            setIsTokenValid(true);
            setIsValidatingToken(false);
        };

        validateToken();
    }, [token]);

    const handleRequestNewReset = async () => {
        setIsRequestingReset(true);
        setResetRequestMessage('');
        
        try {
            // Get email from URL or prompt user
            const email = prompt('Please enter your email address to request a new password reset:');
            if (!email) {
                setIsRequestingReset(false);
                return;
            }
            
            // Call the password reset API
            await requestPasswordReset(email);
            setResetRequestMessage('Password reset link sent! Please check your email.');
            
            // Navigate to login after 2 seconds
            setTimeout(() => {
                navigate('/login');
            }, 2000);
            
        } catch (err) {
            console.error('Error requesting password reset:', err);
            setResetRequestMessage('Failed to send reset link. Please try again.');
            setIsRequestingReset(false);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setMessage('');
        setError('');

        if (!token) {
            setError('Invalid or missing reset token.');
            return;
        }

        const trimmedPassword = newPassword.trim();

        if (trimmedPassword.length < 8) {
            setError('Password must be at least 8 characters long.');
            return;
        }

        if (trimmedPassword !== confirmPassword.trim()) {
            setError('Passwords do not match.');
            return;
        }

        try {
            await resetPassword(token, trimmedPassword, confirmPassword.trim());
            setMessage('Password reset successful. Redirecting to login...');
            setNewPassword('');
            setConfirmPassword('');
            
            // Auto redirect to login page after 2 seconds
            setTimeout(() => {
                navigate('/login');
            }, 2000);
        } catch (err) {
            setError(err.message || 'Failed to reset password. Please try again.');
        }
    };

    const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;
    const isPasswordValid = newPassword.length >= 8;
    const canSubmit = passwordsMatch && isPasswordValid;

    // Show loading while validating token
    if (isValidatingToken) {
        return (
            <CenteredGrid container>
                <Grid item xs={12} sm={8} md={6} lg={4}>
                    <StyledPaper elevation={3}>
                        <Box display="flex" flexDirection="column" alignItems="center" p={4}>
                            <CircularProgress size={40} />
                            <Typography variant="body1" sx={{ mt: 2 }}>
                                Validating reset token...
                            </Typography>
                        </Box>
                    </StyledPaper>
                </Grid>
            </CenteredGrid>
        );
    }

    // Show error if token is invalid/expired
    if (!isTokenValid) {
        return (
            <CenteredGrid container>
                <Grid item xs={12} sm={8} md={6} lg={4}>
                    <StyledPaper elevation={3}>
                        <Box p={4} textAlign="center">
                            <Typography variant="h5" component="h1" gutterBottom color="error">
                                Invalid Reset Link
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                                {tokenError}
                            </Typography>
                            
                            {resetRequestMessage && (
                                <Typography 
                                    variant="body2" 
                                    sx={{ 
                                        mb: 2, 
                                        color: resetRequestMessage.includes('Failed') ? 'error.main' : 'success.main',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {resetRequestMessage}
                                </Typography>
                            )}
                            <Button 
                                variant="contained" 
                                color="primary"
                                onClick={handleRequestNewReset}
                                disabled={isRequestingReset}
                                startIcon={isRequestingReset ? <CircularProgress size={16} /> : null}
                                sx={{ mr: 2 }}
                            >
                                {isRequestingReset ? 'Requesting...' : 'Request New Reset'}
                            </Button>
                            <Button 
                                variant="outlined" 
                                onClick={() => navigate('/login')}
                            >
                                Back to Login
                            </Button>
                        </Box>
                    </StyledPaper>
                </Grid>
            </CenteredGrid>
        );
    }

    // Show password reset form if token is valid
    return (
        <CenteredGrid container>
            <Grid item xs={12} sm={8} md={6} lg={4}>
                <StyledPaper elevation={3}>
                    <Typography variant="h5" component="h1" align="center" gutterBottom>
                        Reset Password
                    </Typography>

                    {message && <SuccessMessage>{message}</SuccessMessage>}
                    {error && <ErrorMessage>{error}</ErrorMessage>}

                    <FormBox component="form" onSubmit={handleSubmit}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            type="password"
                            id="newPassword"
                            label="New Password"
                            name="newPassword"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            error={!!newPassword && newPassword.length < 8}
                            helperText={
                                newPassword && newPassword.length < 8
                                    ? 'Password must be at least 8 characters long.'
                                    : ''
                            }
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            type="password"
                            id="confirmPassword"
                            label="Confirm Password"
                            name="confirmPassword"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            error={
                                !!confirmPassword &&
                                (confirmPassword !== newPassword || newPassword.length < 8)
                            }
                            helperText={
                                confirmPassword && confirmPassword !== newPassword
                                    ? 'Passwords do not match.'
                                    : ''
                            }
                        />
                        <SubmitButton
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={!canSubmit}
                        >
                            Reset Password
                        </SubmitButton>
                    </FormBox>
                </StyledPaper>
            </Grid>
        </CenteredGrid>
    );
}

export default ResetPassword;